import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { addMonths, endOfMonth, endOfWeek, format, parseISO, startOfMonth, startOfWeek } from 'date-fns'
import { toast } from 'sonner'

import { Button } from '@/components/ui/Button'
import { Page, PageHeader } from '@/components/ui/Page'
import { ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { shiftDays, today, type IsoDate } from '@/lib/dates'
import { formatCouple } from '@/lib/format'
import { JOB_KIND_LABEL, jobName } from '@/lib/jobs'
import { deliverableDue, isDelivered } from '@/lib/deliverables'
import { describeError } from '@/lib/supabase'
import { useDeliverySchedule, useSetDelivered, type EditingJob } from '@/features/deliverables/queries'
import { useInquiries } from '@/features/inquiries/queries'
import { useBoard } from '@/features/pipeline/queries'

interface Entry {
  key: string
  date: IsoDate
  label: string
  detail: string
  to: string
  booked: boolean
}

/** A photo or film deliverable on the day it is due, to tick off. */
interface Task {
  key: string
  job: EditingJob
  item: EditingJob['deliverables'][number]
  delivered: boolean
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

/**
 * Calendar: every wedding and booking by month. Booked work is set solid;
 * enquiries and weddings not yet booked are dashed, so a date that is only
 * being asked about reads differently from one that is taken.
 *
 * Photo and film due dates sit on the calendar as tasks with a tick box (27
 * Oct 2026): ticking one marks it delivered, the same as the Editing page.
 * Delivered ones leave the calendar (28 Oct 2026), so only what is still
 * outstanding shows; the Editing page keeps the full record.
 */
export function CalendarPage() {
  const board = useBoard()
  const enquiries = useInquiries('open')
  const [month, setMonth] = useState(() => startOfMonth(parseISO(today())))
  const schedule = useDeliverySchedule(shiftDays(today(), -365))

  const tasks = useMemo(() => {
    const byDate = new Map<IsoDate, Task[]>()
    for (const job of schedule.data ?? []) {
      for (const item of [...job.deliverables].sort((a, b) => a.sort_order - b.sort_order)) {
        if (item.internal || (item.kind !== 'photo' && item.kind !== 'film')) continue
        if (isDelivered(item)) continue
        const due = deliverableDue(item, job)
        if (!due) continue
        const task = { key: item.id, job, item, delivered: false }
        byDate.set(due, [...(byDate.get(due) ?? []), task])
      }
    }
    return byDate
  }, [schedule.data])

  const entries = useMemo(() => {
    const list: Entry[] = []
    for (const job of board.data ?? []) {
      if (job.inquiry && (job.inquiry.status === 'lost' || job.inquiry.status === 'spam')) continue
      list.push({
        key: job.id,
        date: job.event_date,
        label: jobName(job),
        detail: [JOB_KIND_LABEL[job.kind], job.venue?.name ?? job.venue_name].filter(Boolean).join(' · '),
        to: `/weddings/${job.id}`,
        booked: Boolean(job.booked_at),
      })
    }
    for (const inquiry of enquiries.data ?? []) {
      if (inquiry.job || !inquiry.event_date) continue
      list.push({
        key: `enquiry-${inquiry.id}`,
        date: inquiry.event_date,
        label: formatCouple(
          { first_name: inquiry.first_name },
          inquiry.partner_first_name ? { first_name: inquiry.partner_first_name } : null,
        ),
        detail: ['Enquiry', inquiry.venue?.name ?? inquiry.venue_text].filter(Boolean).join(' · '),
        to: `/enquiries/${inquiry.id}`,
        booked: false,
      })
    }
    const byDate = new Map<IsoDate, Entry[]>()
    for (const entry of list) byDate.set(entry.date, [...(byDate.get(entry.date) ?? []), entry])
    return byDate
  }, [board.data, enquiries.data])

  if (board.isPending) return <Loading label="Loading the calendar" />
  if (board.isError) {
    return (
      <Page>
        <ErrorState title="The calendar could not load" detail={describeError(board.error)} onRetry={() => void board.refetch()} />
      </Page>
    )
  }

  const first = format(startOfWeek(startOfMonth(month), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  const last = format(endOfWeek(endOfMonth(month), { weekStartsOn: 1 }), 'yyyy-MM-dd')
  const days: IsoDate[] = []
  for (let day = first; day <= last; day = shiftDays(day, 1)) days.push(day)
  const monthKey = format(month, 'yyyy-MM')
  const now = today()
  const inMonth = days.filter((day) => day.startsWith(monthKey) && (entries.has(day) || tasks.has(day)))
  const bookedCount = inMonth.reduce((sum, day) => sum + (entries.get(day) ?? []).filter((entry) => entry.booked).length, 0)

  return (
    <Page className="max-w-none">
      <PageHeader
        eyebrow="Operations"
        title="Calendar"
        meta={<span>{bookedCount} booked in {format(month, 'MMMM')}</span>}
        actions={
          <>
            <Button variant="ghost" onClick={() => setMonth(addMonths(month, -1))} aria-label="Previous month">
              ←
            </Button>
            <Button variant="ghost" onClick={() => setMonth(startOfMonth(parseISO(today())))}>
              Today
            </Button>
            <Button variant="ghost" onClick={() => setMonth(addMonths(month, 1))} aria-label="Next month">
              →
            </Button>
          </>
        }
      />

      <h2 className="display mb-3 text-[1.5rem]">{format(month, 'MMMM yyyy')}</h2>

      {/* Desktop and tablet: the month grid */}
      <div className="hidden overflow-hidden rounded-xl border-t border-l md:grid md:grid-cols-7">
        {WEEKDAYS.map((day) => (
          <div key={day} className="label border-r border-b px-2 py-1.5">
            {day}
          </div>
        ))}
        {days.map((day) => {
          const outside = !day.startsWith(monthKey)
          return (
            <div key={day} className={cn('min-h-[6.5rem] border-r border-b px-1.5 py-1', outside && 'bg-[var(--color-wash)]')}>
              <p
                className={cn(
                  'mb-1 text-[length:var(--text-2xs)]',
                  outside ? 'text-taupe/60' : 'text-taupe',
                  day === now && 'inline-block rounded-full bg-accent px-1.5 font-semibold text-on-accent',
                )}
                data-numeric
              >
                {Number(day.slice(8))}
              </p>
              <div className="flex flex-col gap-1">
                {(entries.get(day) ?? []).map((entry) => (
                  <EntryChip key={entry.key} entry={entry} />
                ))}
                {(tasks.get(day) ?? []).map((task) => (
                  <TaskChip key={task.key} task={task} late={day < now} />
                ))}
              </div>
            </div>
          )
        })}
      </div>

      {/* Phone: the month as a list */}
      <ul className="border-t border-t-[var(--border-hairline)] md:hidden">
        {inMonth.length === 0 ? <li className="py-6 text-center text-[length:var(--text-sm)] text-taupe">Nothing this month.</li> : null}
        {inMonth.map((day) => (
          <li key={day} className="flex gap-3 border-b py-2">
            <p className="w-12 shrink-0 text-[length:var(--text-2xs)] text-taupe">{format(parseISO(day), 'EEE d')}</p>
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              {(entries.get(day) ?? []).map((entry) => (
                <EntryChip key={entry.key} entry={entry} />
              ))}
              {(tasks.get(day) ?? []).map((task) => (
                <TaskChip key={task.key} task={task} late={day < now} />
              ))}
            </div>
          </li>
        ))}
      </ul>

      <p className="mt-3 text-[length:var(--text-2xs)] text-taupe">
        Solid: booked. Dashed: an enquiry or a wedding not yet booked. Tick boxes: photos and films due that day; tick
        one when it has been delivered and it leaves the calendar (the Editing page keeps the record).
      </p>
    </Page>
  )
}

function EntryChip({ entry }: { entry: Entry }) {
  return (
    <Link
      to={entry.to}
      title={`${entry.label} · ${entry.detail}`}
      className={cn(
        'block truncate rounded-md px-1.5 py-0.5 text-[length:var(--text-2xs)] leading-snug',
        entry.booked ? 'bg-accent text-on-accent hover:opacity-85' : 'border border-dashed border-taupe text-taupe hover:text-ink',
      )}
    >
      <span className="display text-[0.8125rem]">{entry.label}</span>
      <span className="block truncate opacity-70">{entry.detail}</span>
    </Link>
  )
}

function TaskChip({ task, late }: { task: Task; late: boolean }) {
  const setDelivered = useSetDelivered()
  const { job, item, delivered } = task
  return (
    <label
      title={`${item.title} · ${jobName(job)}`}
      className="flex cursor-pointer items-start gap-1.5 rounded-md bg-[var(--surface-card)] px-1.5 py-1 text-[length:var(--text-2xs)] leading-snug shadow-[var(--shadow-card)]"
    >
      <input
        type="checkbox"
        checked={delivered}
        disabled={setDelivered.isPending}
        onChange={(event) =>
          setDelivered.mutate(
            { jobId: job.id, item, delivered: event.target.checked },
            {
              onSuccess: () =>
                toast.success(`${item.title} delivered`, {
                  description: jobName(job),
                  action: { label: 'Undo', onClick: () => setDelivered.mutate({ jobId: job.id, item, delivered: false }) },
                }),
            },
          )
        }
        aria-label={`${item.title}, ${jobName(job)}: delivered`}
        className="check mt-px size-[13px] shrink-0 appearance-none rounded-none border border-ink bg-transparent transition-colors duration-[var(--dur-fast)] checked:bg-ink checked:bg-center checked:bg-no-repeat disabled:opacity-40"
      />
      <span className={cn('min-w-0', delivered && 'text-taupe line-through')}>
        <span className={cn('block truncate font-semibold', !delivered && late && 'text-clay')}>{item.title}</span>
        <span className="block truncate opacity-70">{jobName(job)}</span>
      </span>
    </label>
  )
}
